'use client';
import { PremiumBackground } from '../effects/PremiumBackground';

import React from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowDown, 
  Mic, 
  FileCheck, 
  Cpu, 
  Gauge, 
  TrendingUp, 
  Radar, 
  Grid3X3, 
  AlertTriangle, 
  FileSpreadsheet, 
  Clock, 
  Compass, 
  Award,
  ArrowRight
} from 'lucide-react';
import { LiquidGlassCard } from '../effects/LiquidGlassCard';
// Drop-in Link component for Vite React SPA
const Link = ({
  href,
  children,
  className,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) => (
  <a href={href} className={className} {...props}>
    {children}
  </a>
);

export interface DealReadinessAssessmentProps {
  id?: string;
}

export function DealReadinessAssessment({ id = 'deal-readiness' }: DealReadinessAssessmentProps = {}) {
  // Микроразметка Schema.org для нейросетей и ИИ-поисковиков (Product + HowTo)
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Deal Readiness Assessment™",
    "brand": {
      "@type": "Organization",
      "name": "Structure & Partners"
    },
    "description": "Экспресс-диагностика готовности медицинской клиники к сделке M&A за 1 неделю. Анализ 520 контрольных маркеров, экспресс-аудит 5 базовых документов и выдача 9 институциональных артефактов.",
    "category": "Healthcare M&A Advisory / Pre-Sale Diagnostic",
    "offers": {
      "@type": "Offer",
      "priceCurrency": "RUB",
      "availability": "https://schema.org/InStock",
      "description": "Входной аудит клиники под ключ: интервью с Управляющим партнером Алексеем Киселевым и формирование пакета из 9 артефактов"
    },
    "hasPart": [
      {
        "@type": "HowToStep",
        "name": "C-Level интервью с Управляющим партнером",
        "text": "Сессия на 2-4 часа по выборке из 520 контрольных маркеров для вскрытия структуры выручки, аренды и серых выплат"
      },
      {
        "@type": "HowToStep",
        "name": "Экспресс-анализ 5 базовых документов",
        "text": "Анализ годовой ОСВ, отчета МИС, выписки лицензии Росздравнадзора, договора с пациентом и аренды помещений"
      },
      {
        "@type": "HowToStep",
        "name": "Кросс-синтез 10-го Модуля-Агрегатора",
        "text": "Сведение 9 аналитических доменов и выпуск пакета из 9 институциональных артефактов S&P"
      }
    ]
  };

  // Сетка 9 проприетарных артефактов
  const artifacts = [
    {
      num: "01",
      icon: <Gauge size={18} className="text-[#9A7B3E]" />,
      title: "Deal Readiness Score™",
      badge: "ИНДЕКС ГОТОВНОСТИ (0–100)",
      desc: "Объективный числовой скоринг (0–100). Показывает реальную вероятность закрытия сделки без срыва на этапе финального аудита юристами покупателя."
    },
    {
      num: "02",
      icon: <TrendingUp size={18} className="text-[#9A7B3E]" />,
      title: "Investment Attractiveness Score™",
      badge: "МУЛЬТИПЛИКАТОР (4.0x – 7.5x)",
      desc: "Оценка качества денежного потока, автономности от основателя и рыночной силы бренда для фиксации справедливого мультипликатора (от 4.0x до 7.5x годовой прибыли)."
    },
    {
      num: "03",
      icon: <Radar size={18} className="text-[#9A7B3E]" />,
      title: "Risk Map™",
      badge: "РАДАР УЯЗВИМОСТЕЙ",
      desc: "Наглядный радар уязвимостей по 7 направлениям: финансы, налоги, аренда, медицинское право, ЕГИСЗ, оборудование и кадры. Показывает, где именно сосредоточена угроза."
    },
    {
      num: "04",
      icon: <Grid3X3 size={18} className="text-[#9A7B3E]" />,
      title: "Risk Matrix™",
      badge: "ВЕРОЯТНОСТЬ × УЩЕРБ",
      desc: "Классификация найденных дефектов по шкале «Вероятность вскрытия аудиторами × Финансовый ущерб». Отсекает несущественные мелочи от смертельных ударов по оценке."
    },
    {
      num: "05",
      icon: <AlertTriangle size={18} className="text-[#991B1B]" />,
      title: "Red Flag Register™",
      badge: "СТОП-ФАКТОРЫ СДЕЛКИ",
      desc: "Список токсичных нарушений, способных развалить сделку за день до нотариуса: уголовные риски ст. 238 УК РФ по наркозу, аппараты без регистрации Росздравнадзора, риски слета со ставки 0% по налогу на прибыль."
    },
    {
      num: "06",
      icon: <FileSpreadsheet size={18} className="text-[#9A7B3E]" />,
      title: "Executive Summary™",
      badge: "УПРАВЛЕНЧЕСКОЕ РЕЗЮМЕ",
      desc: "Закрытый 2-страничный меморандум: индикативная стоимость клиники, очищенная прибыль, размер скрытого долга и точная сумма потенциального дисконта в рублях."
    },
    {
      num: "07",
      icon: <Clock size={18} className="text-[#9A7B3E]" />,
      title: "Action Plan™",
      badge: "ТАКТИКА НА 30 ДНЕЙ",
      desc: "Пошаговая инструкция первой необходимости: какие юридические и кадровые бреши нужно закрыть немедленно, чтобы предотвратить внезапные проверки и блокировки счетов."
    },
    {
      num: "08",
      icon: <Compass size={18} className="text-[#9A7B3E]" />,
      title: "Preparation Roadmap™",
      badge: "ДОРОЖНАЯ КАРТА M&A",
      desc: "План предпродажной трансформации актива (сроки, этапы, бюджеты) под выбранный сценарий: продажа как есть, косметическая зачистка или капитальная реструктуризация."
    },
    {
      num: "09",
      icon: <Award size={18} className="text-[#9A7B3E]" />,
      title: "Overall Recommendation™",
      badge: "ВЕРДИКТ АРХИТЕКТОРА",
      desc: "Персональная резолюция Алексея Киселева: стоит ли продавать бизнес сейчас, какую структуру сделки выбрать (продажа долей или продажа имущества) и как забрать максимальный объем чистых денег."
    }
  ];

  return (
    <section 
      id={id} 
      className="relative w-full py-24 md:py-32 bg-[#FAF8F5] text-[#141416] border-t border-[#EAE5DE] overflow-hidden"
      aria-label="Продукт №1: Deal Readiness Assessment"
    >
      {/* ТАКТИЛЬНЫЙ МАКРО-РЕЛЬЕФ */}
      <PremiumBackground variant={2} />

      
      

      <div id="deal-readiness-anchor" className="absolute -top-16 left-0" />
      
      {/* Light subtle archival paper gradient */}
      <div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-b from-[#F2EDE4]/60 to-transparent pointer-events-none" />

      {/* Скрытая разметка JSON-LD для ИИ-поисковиков */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 flex flex-col gap-16 md:gap-20">
        
        {/* =========================================================================
            1. НАДЗАГОЛОВОК, H2 И ПОДЗАГОЛОВОК-РАЗЪЯСНЕНИЕ
            ========================================================================= */}
        <div className="flex flex-col gap-6 max-w-4xl">
          <div className="luxury-eyebrow text-[#8C6D37] flex items-center gap-2.5 font-bold tracking-[0.2em] uppercase">
            <span className="w-1.5 h-1.5 rounded-none bg-[#8C6D37]" />
            ЭТАЖ 6 // ПЕРВЫЙ ШАГ К СДЕЛКЕ // M&A-ДИАГНОСТИКА (ПРОДУКТ №1)
          </div>

          <h2 className="text-3xl sm:text-5xl lg:text-[54px] font-serif font-semibold tracking-tight leading-[1.1] text-[#141416]">
            Шаг 1. Deal Readiness Assessment™: <br />
            <span className="text-[#64748B] italic font-serif">экспресс-диагностика готовности клиники к сделке.</span>
          </h2>

          <div className="flex flex-col gap-4 text-[16px] sm:text-[17px] text-[#334155] leading-[1.65] font-sans font-normal max-w-3xl border-l-[4px] border-[#8C6D37] pl-6 mt-4">
            <p>
              Большинство владельцев боятся слова «аудит» — это долго, дорого и парализует работу врачей.
            </p>
            <p>
              <strong className="text-[#141416] font-semibold">Ассессмент — это не многомесячный Due Diligence. Это стратегический рентген бизнеса за 1 неделю.</strong>
            </p>
            <p>
              Мы не копаем каждую накладную. Наша задача — за 1–2 сессии выявить ключевые уязвимости, 
              оценить вероятность их вскрытия покупателем и рассчитать точную сумму потенциального дисконта.
            </p>
          </div>
        </div>

        {/* =========================================================================
            2. АРХИТЕКТУРА ЭКСПРЕСС-ДИАГНОСТИКИ (3 ПРОСТЫХ ШАГА)
            ========================================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          
          {/* Шаг 1 */}
          <div className="p-8 sm:p-10 flex flex-col justify-between gap-6 bg-white border border-[#EAE5DE] rounded-[2px] shadow-xs relative overflow-hidden group">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between pb-4 border-b border-[#EAE5DE]">
                <span className="luxury-eyebrow text-[#64748B] font-bold tracking-wider uppercase">
                  ШАГ 1. C-LEVEL ИНТЕРВЬЮ
                </span>
                <Mic size={16} className="text-[#8C6D37]" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-[#141416] leading-snug">
                Глубинное интервью с Управляющим партнером (2–4 часа)
              </h3>
              <p className="text-[14px] text-[#334155] leading-relaxed font-sans font-normal">
                Лично проводит сессию по выборке из проприетарной базы <strong>520 контрольных маркеров</strong>. 
                За 120–240 минут мы вскрываем реальную структуру выручки, серый контур выплат врачам, 
                устойчивость аренды и скрытые обязательства.
              </p>
            </div>
            <div className="font-mono text-[11px] text-[#64748B] bg-[#F7F4EE] p-3 border border-[#EAE5DE] rounded-[2px] uppercase font-bold tracking-wider">
              Фокус: Структура выручки и личные расходы
            </div>
          </div>

          {/* Шаг 2 */}
          <div className="p-8 sm:p-10 flex flex-col justify-between gap-6 bg-white border border-[#EAE5DE] rounded-[2px] shadow-xs relative overflow-hidden group">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between pb-4 border-b border-[#EAE5DE]">
                <span className="luxury-eyebrow text-[#64748B] font-bold tracking-wider uppercase">
                  ШАГ 2. АНАЛИЗ 5 СРЕЗОВ
                </span>
                <FileCheck size={16} className="text-[#8C6D37]" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-[#141416] leading-snug">
                Экспресс-анализ 5 базовых документов (Без нагрузки на бухгалтерию)
              </h3>
              <p className="text-[14px] text-[#334155] leading-relaxed font-sans font-normal">
                Мы не требуем терабайты архивов. Нам нужны только <strong>5 файлов</strong>: годовая оборотно-сальдовая 
                ведомость (ОСВ), сводный отчет выручки из МИС, выписка лицензии Росздравнадзора, типовой договор с 
                пациентом и договор аренды помещений.
              </p>
            </div>
            <div className="font-mono text-[11px] text-[#64748B] bg-[#F7F4EE] p-3 border border-[#EAE5DE] rounded-[2px] uppercase font-bold tracking-wider">
              Без выгрузки первичных накладных
            </div>
          </div>

          {/* Шаг 3 */}
          <div className="p-8 sm:p-10 border border-[#8C6D37]/50 flex flex-col justify-between gap-6 bg-[#8C6D37]/10 rounded-[2px] relative overflow-hidden shadow-xs">
            
            {/* Метка (Pill Capsule) */}
            <div className="absolute -top-3 right-6 bg-[#8C6D37] text-white font-mono text-[10px] pill-capsule uppercase tracking-[0.2em] font-bold shadow-sm px-3.5 py-1">
              PROPRIETARY ENGINE
            </div>
            
            <div className="flex flex-col gap-4 pt-2">
              <div className="flex items-center justify-between pb-4 border-b border-[#8C6D37]/20">
                <span className="luxury-eyebrow text-[#8C6D37] font-bold tracking-wider uppercase">
                  ШАГ 3. КРОСС-СИНТЕЗ
                </span>
                <Cpu size={16} className="text-[#8C6D37]" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-[#141416] leading-snug">
                Запуск 10-го Модуля-Агрегатора (The Synthesis Engine)
              </h3>
              <p className="text-[14px] text-[#334155] leading-relaxed font-sans font-normal">
                Система сводит данные 9 аналитических доменов и формирует доказательный пакет 
                из <strong>9 институциональных артефактов S&P</strong>. Вы получаете на руки твердую оцифровку рисков 
                глазами инвест-комитетов.
              </p>
            </div>
            <div className="font-mono text-[11px] text-[#8C6D37] bg-white/80 p-3 border border-[#8C6D37]/30 rounded-[2px] uppercase font-bold tracking-wider text-center">
              Генерация пакета из 9 документов
            </div>
          </div>
        </div>

        {/* =========================================================================
            3. ВЫХОДНОЙ ПАКЕТ: 9 ПРОПРИЕТАРНЫХ АРТЕФАКТОВ S&P (3X3 GRID)
            ========================================================================= */}
        <div className="flex flex-col gap-8">
          <div className="flex items-center justify-between">
            <span className="luxury-eyebrow text-[#64748B] font-bold tracking-wider uppercase">
              ВЫХОДНОЙ ПАКЕТ // 9 ИНСТИТУЦИОНАЛЬНЫХ АРТЕФАКТОВ S&P:
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {artifacts.map((art, idx) => (
              <div 
                key={idx}
                className={`p-8 flex flex-col justify-between gap-6 rounded-[2px] shadow-xs relative overflow-hidden group ${
                  art.num === '05' 
                    ? 'border border-rose-300 bg-rose-50/70' 
                    : 'bg-white border border-[#EAE5DE]'
                }`}
              >
                <div className="flex flex-col gap-4">
                  {/* Верхняя строка карточки */}
                  <div className="flex items-center justify-between pb-3 border-b border-[#EAE5DE]">
                    <div className="flex items-center gap-2.5">
                      {art.icon}
                      <span className={`luxury-eyebrow font-bold tracking-wider uppercase ${art.num === '05' ? 'text-rose-800' : 'text-[#64748B]'}`}>
                        {art.badge}
                      </span>
                    </div>
                    <span className="font-mono text-[11px] text-[#64748B] font-bold">
                      [{art.num}]
                    </span>
                  </div>

                  {/* Заголовок */}
                  <h4 className="text-[17px] font-sans font-semibold text-[#141416] leading-snug">
                    {art.title}
                  </h4>

                  {/* Описание */}
                  <p className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal">
                    {art.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* =========================================================================
            4. ИТОГОВЫЙ ЭКОНОМИЧЕСКИЙ ВЫВОД (CALLOUT BANNER)
            ========================================================================= */}
        <div className="p-8 sm:p-10 bg-[#8C6D37]/10 border border-[#8C6D37]/40 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8 rounded-[2px] shadow-xs">
          <div className="flex flex-col gap-2.5 max-w-3xl">
            <div className="luxury-eyebrow text-[#8C6D37] flex items-center gap-2 font-bold tracking-[0.1em]">
              <span>РЕЗУЛЬТАТ ДИАГНОСТИКИ DEAL READINESS ASSESSMENT™:</span>
            </div>
            <p className="text-[15px] sm:text-[16px] text-[#141416] leading-relaxed font-sans font-normal">
              Вы перестаете гадать, сколько стоит ваш бизнес. Вы получаете на руки полный комплект 
              документов инвестиционного класса, видите каждую уязвимость глазами покупателя 
              и точно знаете, как защитить цену клиники на переговорах.
            </p>
          </div>

          <Link
            href="#diagnostic"
            className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-3.5 bg-[#141416] text-white font-mono text-[11px] uppercase tracking-[0.15em] font-bold hover:bg-[#8C6D37] transition-all rounded-[2px] shadow-xs"
          >
            <span>Запросить ассессмент</span>
            <ArrowRight size={14} className="text-[#8C6D37] group-hover:text-white" />
          </Link>
        </div>

        {/* =========================================================================
            5. НАВИГАТОР ПЕРЕХОДА К БЛОКУ 7
            ========================================================================= */}
        <div className="flex justify-center pt-4">
          <Link 
            href="#fork" 
            className="group inline-flex items-center gap-2 font-mono text-[11px] text-[#64748B] hover:text-[#141416] uppercase tracking-[0.15em] font-semibold transition-all py-3 px-6 hover:bg-black/5 border border-transparent hover:border-[#EAE5DE] rounded-[2px]"
          >
            <span>[ ПЕРЕЙДИТЕ К ШАГУ 2: ВЫБОР СТРАТЕГИЧЕСКОГО СЦЕНАРИЯ ПОСЛЕ ДИАГНОЗА ↓ ]</span>
          </Link>
        </div>

      </div>
    </section>
  );
}
